import { axiosJSONInstance } from '@/config';
import {
  ActivityLogResponse,
  AvailableCodResponse,
  Remittance,
  RemittanceListResponse,
  RemittanceResponse,
  RemittanceSingleResponse,
  TranslationListResponse,
} from '@/types'; // Ensure this path is correct based on your folder structure

// Fetch all remittances
export const fetchRemittances = async (
  page: number,
  pageSize?: number,
  statusFilter?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<RemittanceListResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    perpage: String(pageSize || 25),
  });
  if (statusFilter) params.append('filter_status', statusFilter);
  if (dateFrom) params.append('filter_created_at_from', dateFrom);
  if (dateTo) params.append('filter_created_at_to', dateTo);

  const response = await axiosJSONInstance.get(`/merchants/remittance?${params.toString()}`);
  return response.data;
};

// Fetch Remittance ActivityLogs
export const fetchRemittanceActivityLogByRemittanceUID = async (
  uid: string,
  page = 1,
  perpage = 50,
): Promise<ActivityLogResponse> => {
  const response = await axiosJSONInstance.get(`merchants/remittance/${uid}/activity-logs`, {
    params: {
      perpage,
      page,
    },
  });

  return response.data;
};

// Fetch Remittance UpdatesComment
export const fetchRemittanceUpdatesCommentByRemittanceUID = async (
  uid: string,
  page = 1,
  perpage = 50,
): Promise<ActivityLogResponse> => {
  const response = await axiosJSONInstance.get(
    `merchants/remittance/${uid}/activity-logs/recalculate-transaction`,
    {
      params: {
        perpage,
        page,
      },
    },
  );

  return response.data;
};

// Fetch all transactions
export const fetchTransactions = async (
  page: number,
  pageSize?: number,
  transactionFilter?: URLSearchParams,
): Promise<TranslationListResponse> => {
  const response = await axiosJSONInstance.get(
    `/merchants/transactions?${transactionFilter?.toString()}`,
    {
      params: {
        perpage: pageSize,
        page: page,
      },
    },
  );
  return response.data;
};

// Fetch a remittance by UID
export const fetchRemittanceByUID = async (uid: string): Promise<RemittanceSingleResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/remittance/${uid}`);
  return response.data;
};

// Fetch orders in a remittance (server-paginated — load-more, 25/batch)
export const fetchRemittanceOrders = async (uid: string, page = 1, perpage = 25): Promise<any> => {
  const response = await axiosJSONInstance.get(`/merchants/remittance/${uid}/orders`, {
    params: { page, perpage },
  });
  return response.data;
};

// Create a new remittance
export const createRemittance = async (remittance: Remittance): Promise<RemittanceResponse> => {
  const response = await axiosJSONInstance.post('/merchants/remittance', remittance);
  return response.data;
};
// Delete a remittance by UID
export const deleteRemittance = async (uid: number): Promise<void> => {
  await axiosJSONInstance.delete(`/merchants/remittance/${uid}`);
};

// Update a remittance's status
export const updateRemittanceStatus = async (
  uid: number,
  status: 'initiated' | 'in-transit' | 'delivered',
) => {
  const response = await axiosJSONInstance.patch(`/merchants/remittance/${uid}/status/${status}`);
  return response.data;
};

// fetch available COD
export const fetchAvailableCod = async (): Promise<AvailableCodResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/transactions/available-cod`);
  return response.data;
};

// Fetch labels Remittance by UID
export const fetchLabelRemittanceByUID = async (uid: string): Promise<File> => {
  const response = await axiosJSONInstance.get(`/merchants/remittance/${uid}/statement`, {
    responseType: 'blob',
    timeout: 600000, //10 minutes
  });
  return response.data;
};

// Download remittance statement as Excel (sync, direct download)
export const fetchRemittanceExcel = async (uid: string): Promise<File> => {
  const response = await axiosJSONInstance.get(`/merchants/remittance/${uid}/statement-csv`, {
    responseType: 'blob',
    timeout: 600000,
  });
  return response.data;
};

// Download proof of remittance delivery as PDF
export const fetchRemittanceProof = async (uid: string): Promise<Blob> => {
  const response = await axiosJSONInstance.get(`/merchants/remittance/${uid}/proof`, {
    responseType: 'blob',
    timeout: 30000,
  });
  return response.data;
};

// Trigger async remittance export (PDF generation)
export const createRemittanceExport = async (uid: string) => {
  const response = await axiosJSONInstance.post(`/merchants/remittance/${uid}/export`);
  return response.data;
};

// Fetch latest remittance export status and file URL
export const fetchRemittanceExport = async (uid: string) => {
  const response = await axiosJSONInstance.get(`/merchants/remittance/${uid}/export`);
  return response.data;
};

// Finance overview — balance, total sales, chart data
export interface FinanceOverviewResponse {
  status: string;
  code: number;
  message: string;
  object: {
    available_cod: number;
    pending_cod: number;
    total_sales: number;
    chart: Array<{ label: string; value: number }>;
    /**
     * Advance COD withdrawal lock (W5, FinanceOverviewController.php:68).
     * Authoritative, permission-safe (this endpoint is FINANCE-gated, not
     * ADVANCE_COD-gated) source for the Withdraw lock. True whenever the
     * merchant is enrolled and remittance withdrawal is paused.
     */
    remittance_locked?: boolean;
  };
}

export const fetchFinanceOverview = async (period?: string): Promise<FinanceOverviewResponse> => {
  const params = new URLSearchParams();
  if (period) params.append('period', period);
  const query = params.toString();
  const response = await axiosJSONInstance.get(
    `/merchants/finance-overview${query ? `?${query}` : ''}`,
  );
  return response.data;
};
